<template lang="pug">
b-row
  b-col(cols="12")
    div.ari-wizard-form
      form-wizard.wizard-vertical.mb-3(
        color="#498ceb",
        layout="horizontal",
        :title="null",
        :subtitle="null",
        :finish-button-text="$t('message.submit')",
        :back-button-text="$t('message.previous')",
        :next-button-text="$t('message.next')",
        ref="ariFormRef"
      )
        // Custom footer
        template(v-slot:footer="slotProps")
          div(class="wizard-footer d-flex justify-content-between align-items-center")
            div
              b-button(
                variant="outline-secondary"
                @click="goToPreviousTab"
                :disabled="slotProps.activeTabIndex === 0"
              )
                | < &nbsp; {{ $t('message.previous') }}

              b-badge(
                v-if="isAutosaving"
                variant="light-info"
                class="ml-2"
              )
                span.spinner-border.spinner-border-sm.mr-1
                | Guardando borrador...

              b-badge(
                v-else-if="lastAutosaveTime"
                variant="light-success"
                class="ml-2"
              )
                | ✓ Guardado automático {{ formatAutosaveTime(lastAutosaveTime) }}

            div(class="ml-auto d-flex align-items-center")
              b-button(
                v-if="!slotProps.isLastStep"
                variant="primary"
                class="mr-2"
                @click="goToNextTab"
              )
                | {{ $t('message.next') }} &nbsp; >

              b-button(
                variant="success"
                @click="handleSubmit"
              )
                | {{ $t('message.submit') }}

        // PASO 1 - Selección de Tratamiento
        tab-content(:title="$t('Tratamiento')")
          validation-observer(ref="step1Tab", tag="form")
            b-row
              b-col(md="12")
                b-form-group(label="Seleccionar Tratamiento *", label-for="treatment")
                  validation-provider(name="Tratamiento", rules="required", v-slot="{ errors }")
                    v-select(
                      v-model="formData.treatment_id"
                      label="nombre"
                      :options="availableTreatments"
                      :reduce="treatment => treatment._id"
                      placeholder="Seleccione un tratamiento"
                      @input="handleTreatmentSelect"
                    )
                    small(class="text-danger") {{ errors[0] }}

            b-row(v-if="selectedTreatment")
              b-col(md="12")
                b-form-group(label="ID Tratamiento", label-for="treatmentId")
                  b-form-input(v-model="selectedTreatment.treatmentId", disabled, :style="{ width: '25%' }")

            b-row(v-if="selectedTreatment")
              b-col(md="12")
                b-form-group(label="Nombre", label-for="treatmentName")
                  b-form-input(v-model="selectedTreatment.nombre", disabled, :style="{ width: '75%' }")

            b-row(v-if="selectedTreatment")
              b-col(md="12")
                b-form-group(label="Rol", label-for="rol")
                  b-form-input(v-model="selectedTreatment.rol", disabled, :style="{ width: '25%' }")

            b-row(v-if="selectedTreatment")
              b-col(md="12")
                b-form-group(label="Datos Sensibles", label-for="hasSensitive")
                  b-form-checkbox(v-model="selectedTreatment.hasSensitive", disabled, switch)

            b-row(v-if="selectedTreatment")
              b-col(md="12")
                b-form-group(label="Datos de NNA", label-for="hasMinors")
                  b-form-checkbox(v-model="selectedTreatment.hasMinors", disabled, switch)

            b-row(v-if="selectedTreatment")
              b-col(md="12")
                b-form-group(label="Datos Biométricos", label-for="hasBiometric")
                  b-form-checkbox(v-model="selectedTreatment.hasBiometric", disabled, switch)

            b-alert(v-if="existingARIWarning", variant="warning", show)
              | Este tratamiento ya tiene un ARI asociado.
              | &nbsp;
              b-link(:to="{ name: 'privacy-inherent-risk-edit', params: { id: existingARIId } }")
                | Editar existente

        // PASO 2 - Factores de Probabilidad
        tab-content(:title="$t('Probabilidad')")
          validation-observer(ref="step2Tab", tag="form")
            b-alert(variant="info", show, class="mb-3")
              | Evalúe cada factor de probabilidad seleccionando un score de 1 a 5.
              | Use N/A si el factor no aplica a este tratamiento.

            b-row
              b-col(v-for="factor in probabilityFactors", :key="factor.id", md="12")
                validation-provider(:name="factor.name", rules="required", v-slot="{ errors }")
                  score-dropdown(
                    v-model="formData.scores[factor.id]"
                    :label="factor.name + ' *'"
                    :field-id="factor.id"
                    :rubric="factor.rubric"
                    :style="{ width: '33.33%' }"
                  )
                  small(class="text-danger d-block mb-1") {{ errors[0] }}

        // PASO 3 - Factores de Impacto
        tab-content(:title="$t('Impacto')")
          validation-observer(ref="step3Tab", tag="form")
            b-alert(variant="info", show, class="mb-3")
              | Evalúe cada factor de impacto seleccionando un score de 1 a 5.
              | Use N/A si el factor no aplica a este tratamiento.

            b-row
              b-col(v-for="factor in impactFactors", :key="factor.id", md="12")
                validation-provider(:name="factor.name", rules="required", v-slot="{ errors }")
                  score-dropdown(
                    v-model="formData.scores[factor.id]"
                    :label="factor.name + ' *'"
                    :field-id="factor.id"
                    :rubric="factor.rubric"
                    :style="{ width: '33.33%' }"
                  )
                  small(class="text-danger") {{ errors[0] }}

        // PASO 4 - Factores EIPD
        tab-content(:title="$t('EIPD')")
          validation-observer(ref="step4Tab", tag="form")
            b-alert(variant="info", show, class="mb-3")
              | Evalúe los factores de EIPD (Evaluación de Impacto de Protección de Datos).
              | Todos los factores son obligatorios (score 1-5).

            b-row
              b-col(v-for="factor in eipdFactors", :key="factor.id", md="12")
                validation-provider(:name="factor.name", rules="required", v-slot="{ errors }")
                  score-dropdown(
                    v-model="formData.scores[factor.id]"
                    :label="factor.name + ' *'"
                    :field-id="factor.id"
                    :rubric="factor.rubric"
                    :style="{ width: '33.33%' }"
                  )
                  small(class="text-danger") {{ errors[0] }}

        // PASO 5 - Obligatoriedad Legal
        tab-content(:title="$t('Obligatoriedad Legal')")
          validation-observer(ref="step5Tab", tag="form")
            b-alert(variant="info", show, class="mb-3")
              | Marque las hipótesis de obligatoriedad legal que apliquen según Ley 21.719.

            b-row
              b-col(md="12")
                legal-checkbox(
                  v-model="formData.legalObligations.ol_a"
                  field-id="ol_a"
                  title="OL-a: Evaluación sistemática + decisiones automatizadas con efectos jurídicos"
                  description="Evaluación sistemática y exhaustiva de aspectos personales de los titulares basada en tratamiento o decisiones automatizadas, como la elaboración de perfiles, que produzcan en ellos efectos jurídicos significativos."
                  legal-reference="OBLIGATORIEDAD LEGAL (a) — Ley 21.719"
                )

                legal-checkbox(
                  v-model="formData.legalObligations.ol_b"
                  field-id="ol_b"
                  title="OL-b: Tratamiento masivo o a gran escala"
                  description="Tratamiento masivo de datos o a gran escala."
                  legal-reference="OBLIGATORIEDAD LEGAL (b) — Ley 21.719"
                )

                legal-checkbox(
                  v-model="formData.legalObligations.ol_c"
                  field-id="ol_c"
                  title="OL-c: Observación/monitoreo sistemático de zona pública"
                  description="Tratamiento que implique observación o monitoreo sistemático de una zona de acceso público."
                  legal-reference="OBLIGATORIEDAD LEGAL (c) — Ley 21.719"
                )

                legal-checkbox(
                  v-model="formData.legalObligations.ol_d"
                  field-id="ol_d"
                  title="OL-d: Sensibles sin excepción de consentimiento"
                  description="Tratamiento de datos sensibles y especialmente protegidos, en las hipótesis de excepción del consentimiento."
                  legal-reference="OBLIGATORIEDAD LEGAL (d) — Ley 21.719"
                )

        // PASO 6 - Cálculo y Resultado
        tab-content(:title="$t('Resultado')")
          validation-observer(ref="step6Tab", tag="form")
            b-row
              b-col(md="12")
                b-card(title="Resultados Calculados", border-variant="primary")
                  b-row
                    b-col(md="3")
                      b-form-group(label="P (Probabilidad)")
                        b-badge(variant="light-primary", class="font-weight-bold p-2 w-100")
                          | {{ calculatedResults.P || 'N/A' }}

                    b-col(md="3")
                      b-form-group(label="I (Impacto)")
                        b-badge(variant="light-info", class="font-weight-bold p-2 w-100")
                          | {{ calculatedResults.I || 'N/A' }}

                    b-col(md="3")
                      b-form-group(label="Riesgo Inherente")
                        b-badge(variant="light-dark", class="font-weight-bold p-2 w-100")
                          | {{ calculatedResults.risk || 'N/A' }}

                    b-col(md="3")
                      b-form-group(label="Nivel de Riesgo")
                        risk-level-badge(:level="calculatedResults.level")

                  b-row
                    b-col(md="12")
                      b-form-group(label="EIPD Requerida (Automático)")
                        b-badge(
                          :variant="calculatedResults.requiresEipdAuto ? 'danger' : 'success'"
                          class="font-weight-bold p-2"
                        )
                          | {{ calculatedResults.requiresEipdAuto ? 'Sí' : 'No' }}

            b-row(class="mt-3")
              b-col(md="12")
                b-card(title="Override EIPD (Opcional)", border-variant="warning")
                  b-form-group(label="Decisión sobre EIPD")
                    b-form-radio-group(v-model="eipdDecisionMode")
                      b-form-radio(value="auto")
                        | Usar decisión automática

                      b-form-radio(value="manual")
                        | Forzar manualmente

                  div(v-if="eipdDecisionMode === 'manual'", class="mt-3")
                    b-form-group(label="EIPD Requerida")
                      b-form-radio-group(v-model="formData.eipdOverride")
                        b-form-radio(:value="true") Sí
                        b-form-radio(:value="false") No

                    b-form-group(label="Justificación (obligatoria) *")
                      validation-provider(name="Justificación", :rules="eipdDecisionMode === 'manual' ? 'required' : ''", v-slot="{ errors }")
                        b-form-textarea(
                          v-model="formData.eipdOverrideJustification"
                          placeholder="Explique por qué está forzando esta decisión..."
                          rows="3"
                          :state="eipdJustificationState"
                        )
                        small(class="text-danger") {{ errors[0] }}

                  b-alert(variant="info", :show="eipdDecisionMode === 'manual'", class="mt-2")
                    | <strong>Importante:</strong> El override manual quedará registrado en el historial de cambios.

    // Audit Log Table
    audit-log-table(
      v-if="isEdit && data"
      :history="data.history || []"
      :field-translations="{\
        treatment_id: $t('label.treatment'),\
        scores: $t('label.scores'),\
        legalObligations: $t('label.legalObligations'),\
        calculatedP: $t('label.calculatedProbability'),\
        calculatedI: $t('label.calculatedImpact'),\
        inherentRisk: $t('message.tableHeader.inherentRisk'),\
        inherentLevel: $t('label.inherentLevel'),\
        requiresEipdAuto: $t('label.requiresEIPDAuto'),\
        eipdOverride: $t('label.eipdOverride'),\
        eipdOverrideJustification: $t('label.eipdOverrideJustification'),\
        eipdFinal: $t('label.eipdFinal')\
      }"
    )
</template>

<script>
import { onMounted, watch } from '@vue/composition-api'
import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import ScoreDropdown from '../../components/ScoreDropdown.vue'
import LegalCheckbox from '../../components/LegalCheckbox.vue'
import RiskLevelBadge from '../../components/RiskLevelBadge.vue'
import AuditLogTable from '@/components/AuditLogTable.vue'
import useARIForm from './useARIForm'
import { BAlert, BFormCheckbox, BBadge, BFormRadio, BFormRadioGroup, BFormInvalidFeedback, BLink } from "bootstrap-vue"

export default {
  name: 'ARIForm',
  components: {
    FormWizard,
    TabContent,
    ValidationProvider,
    ValidationObserver,
    vSelect,
    ScoreDropdown,
    LegalCheckbox,
    RiskLevelBadge,
    AuditLogTable,
    BAlert,
    BFormCheckbox,
    BBadge,
    BFormRadio,
    BFormRadioGroup,
    BFormInvalidFeedback,
    BLink,
  },
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    onSaveDraft: {
      type: Function,
      default: null,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    currentAriId: {
      type: String,
      default: null,
    },
  },
  setup(props, context) {
    const {
      ariFormRef,
      availableTreatments,
      selectedTreatment,
      existingARIWarning,
      existingARIId,
      eipdDecisionMode,
      isAutosaving,
      lastAutosaveTime,
      formData,
      calculatedResults,
      probabilityFactors,
      impactFactors,
      eipdFactors,
      eipdJustificationState,
      canNavigateFromStep1,
      step1Tab,
      step2Tab,
      step3Tab,
      step4Tab,
      step5Tab,
      step6Tab,
      fetchTreatments,
      handleTreatmentSelect,
      handleSubmit,
      goToNextTab,
      goToPreviousTab,
      formatAutosaveTime,
    } = useARIForm(props, context)

    onMounted(async () => {
      await fetchTreatments()
    })

    // Watch for data changes (for edit mode)
    watch(() => props.data, (newData) => {
      if (newData && Object.keys(newData).length > 0) {
        Object.assign(formData, newData)

        // If there's an EIPD override justification, set decision mode to manual
        if (newData.eipdOverrideJustification && newData.eipdOverrideJustification.trim()) {
          eipdDecisionMode.value = 'manual'
        }

        if (newData.treatment_id) {
          // Only call handleTreatmentSelect if treatments are loaded
          if (availableTreatments.value.length > 0) {
            handleTreatmentSelect(newData.treatment_id)
          } else {
            // Wait for treatments to load, then call handleTreatmentSelect
            const unwatch = watch(availableTreatments, (treatments) => {
              if (treatments.length > 0) {
                handleTreatmentSelect(newData.treatment_id)
                unwatch() // Stop watching after first call
              }
            })
          }
        }
      }
    }, { immediate: true, deep: true })

    return {
      ariFormRef,
      availableTreatments,
      selectedTreatment,
      existingARIWarning,
      existingARIId,
      eipdDecisionMode,
      isAutosaving,
      lastAutosaveTime,
      formData,
      calculatedResults,
      probabilityFactors,
      impactFactors,
      eipdFactors,
      eipdJustificationState,
      canNavigateFromStep1,
      step1Tab,
      step2Tab,
      step3Tab,
      step4Tab,
      step5Tab,
      step6Tab,
      handleTreatmentSelect,
      handleSubmit,
      goToNextTab,
      goToPreviousTab,
      formatAutosaveTime,
    }
  },
}
</script>

<style lang="scss" scoped>
.wizard-footer {
  padding: 1rem 0;
  border-top: 1px solid #e0e0e0;
  margin-top: 1rem;
}

/* Mejorar contraste de inputs con fondo gris más oscuro */
::v-deep .form-control {
  background-color: #e9ecef !important;
}

::v-deep .form-control:disabled {
  background-color: #c6c6c6 !important;
}

::v-deep textarea.form-control {
  background-color: #e9ecef !important;
}

::v-deep textarea.form-control:disabled {
  background-color: #c6c6c6 !important;
}

::v-deep input.form-control {
  background-color: #e9ecef !important;
}

::v-deep input.form-control:disabled {
  background-color: #c6c6c6 !important;
}

// Para v-select
::v-deep .vs__dropdown-toggle {
  background-color: #e9ecef !important;
}

::v-deep .v-select.vs--disabled .vs__dropdown-toggle {
  background-color: #c6c6c6 !important;
}

// Para score-dropdown (también usa v-select internamente)
::v-deep .score-dropdown .vs__dropdown-toggle {
  background-color: #e9ecef !important;
}
</style>

<style lang="scss">
// Estilos globales para este componente específico (sin scoped para penetrar en v-select)
.ari-wizard-form {
  .v-select .vs__dropdown-toggle {
    background-color: #e9ecef !important;
  }

  .v-select.vs--disabled .vs__dropdown-toggle {
    background-color: #c6c6c6 !important;
    opacity: 1 !important;
  }

  // Para score-dropdown específicamente
  .score-dropdown-wrapper .v-select .vs__dropdown-toggle {
    background-color: #e9ecef !important;
  }

  // Inputs disabled con más especificidad
  input.form-control[disabled],
  input.form-control:disabled {
    background-color: #c6c6c6 !important;
    opacity: 1 !important;
  }

  textarea.form-control[disabled],
  textarea.form-control:disabled {
    background-color: #c6c6c6 !important;
    opacity: 1 !important;
  }

  .form-control[disabled],
  .form-control:disabled {
    background-color: #c6c6c6 !important;
    opacity: 1 !important;
  }

  // Switches disabled con color gris oscuro
  .custom-switch input[type="checkbox"]:disabled ~ .custom-control-label::before {
    background-color: #c6c6c6 !important;
    border-color: #c6c6c6 !important;
    opacity: 1 !important;
  }

  .custom-switch input[type="checkbox"]:disabled:checked ~ .custom-control-label::before {
    background-color: #c6c6c6 !important;
    border-color: #c6c6c6 !important;
    opacity: 1 !important;
  }

  .custom-control-input:disabled ~ .custom-control-label {
    opacity: 1 !important;
  }
}
</style>
